/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Button, Row, Col } from "@duxton-web/ui"
import { AddCircleFilled } from "@duxton-web/icons"
import { useTranslation } from "common/utils/i18n"
import { ItemSearchBar } from "components/search/ItemSearchBar"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory, EventEntryPoint } from "constants/tracking"
import { Item } from "types/models/menu"
import { headerActions } from "styles/common"
import styles from "./ItemListHeader.modules.css"

interface ItemListHeaderProps {
  onSetFilter(filter: string): void
  onClear?: () => void
  filteredItems: Item[]
  hasCreateAction?: boolean
  searchError?: string
  hideDropdownResults?: boolean
}

export const ItemListHeader: FC<ItemListHeaderProps> = ({
  onSetFilter,
  onClear,
  filteredItems,
  hasCreateAction = true,
  searchError = "",
  hideDropdownResults = false,
}: ItemListHeaderProps) => {
  const dispatch = useDispatch()

  const {
    merchantID,
    isCreateItemAllowed,
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)

  const handleClickCreate = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuItemsTab,
      action: EventAction.clickAddItem,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        ENTRY_POINT: EventEntryPoint.itemListingPage,
      },
    })

    dispatch(
      actionCreator.menu.toggleDrawer({
        item: null,
        showItem: true,
      }),
    )
  }

  const textCreate = useTranslation("menu.items.buttons.createNewItem")

  return (
    <Row>
      <Col span={4}>
        <div css={headerActions}>
          {hasCreateAction && (
            <Button
              icon={<AddCircleFilled />}
              data-testid="action-button"
              onClick={handleClickCreate}
              disabled={!isCreateItemAllowed}
            >
              {textCreate}
            </Button>
          )}
        </div>
      </Col>
      <Col span={8} />
      <Col span={12}>
        <div>
          <ItemSearchBar
            className={styles.searchBarWrapper}
            data-testid="search-bar"
            onSelect={item => onSetFilter(item?.itemName ?? "")}
            onSearch={value => onSetFilter(value ?? "")}
            onClear={onClear || (() => onSetFilter(""))}
            itemsFromParent={filteredItems}
            hideDropdownResults={hideDropdownResults}
          />
          {searchError && (
            <div className={styles.searchError}>{searchError}</div>
          )}
        </div>
      </Col>
    </Row>
  )
}
